import { useState, useRef, useCallback, useEffect, type ReactNode } from 'react';
import { AlertCircle, Plus } from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════
   KanbanBoard — Board genérico drag & drop horizontal
   Com suporte a activeColumn, opacidade e animação
   ═══════════════════════════════════════════════════════════════ */

export interface KanbanColumn {
  id: string;
  label: string;
  color: string;
}

export interface KanbanBoardProps<T extends { id: string }> {
  columns: KanbanColumn[];
  items: T[];
  getColumnId: (item: T) => string;
  onDrop: (itemId: string, columnId: string) => void | Promise<void>;
  onCardClick?: (item: T) => void;
  renderCard: (item: T, column: KanbanColumn) => ReactNode;
  isLoading?: boolean;
  loadingText?: string;
  emptyText?: string;
  className?: string;
  hideNav?: boolean;
  /** ID da coluna atualmente selecionada (para highlight) */
  activeColumn?: string | null;
  /** Callback ao clicar no "+" de uma coluna — recebe o columnId */
  onAddClick?: (columnId: string) => void;
  disableCollapse?: boolean;
  renderColumnHeaderRight?: (column: KanbanColumn) => ReactNode;
  renderColumnSubHeader?: (column: KanbanColumn) => ReactNode;
  /** Direção do fluxo das colunas: horizontal ou vertical (default: horizontal) */
  direction?: 'horizontal' | 'vertical';
  style?: React.CSSProperties;
}

export function KanbanBoard<T extends { id: string }>({
  columns,
  items,
  getColumnId,
  onDrop,
  onCardClick,
  renderCard,
  isLoading = false,
  loadingText = 'Carregando...',
  emptyText = 'Solte cards aqui',
  className = '',
  hideNav = false,
  activeColumn = null,
  style,
  onAddClick,
  disableCollapse = false,
  renderColumnHeaderRight,
  renderColumnSubHeader,
  direction = 'horizontal',
}: KanbanBoardProps<T>) {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  /** Coluna cujo header-circle já fez scroll (segundo clique = add) */
  const [scrolledColumn, setScrolledColumn] = useState<string | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const columnRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const handleDragStart = (id: string) => setDraggedId(id);

  /* ── Drag-to-scroll horizontal (desktop) ── */
  useEffect(() => {
    if (direction === 'vertical') return;
    const el = boardRef.current;
    if (!el) return;
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    const onMouseDown = (e: MouseEvent) => {
      // Só ativa no background do board, não em cards/botões
      if ((e.target as HTMLElement).closest('.kb-card, button, a, input')) return;
      isDown = true;
      el.style.cursor = 'grabbing';
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
    };
    const onMouseLeave = () => { isDown = false; el.style.cursor = ''; };
    const onMouseUp = () => { isDown = false; el.style.cursor = ''; };
    const onMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      el.scrollLeft = scrollLeft - (x - startX);
    };

    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mouseleave', onMouseLeave);
    el.addEventListener('mouseup', onMouseUp);
    el.addEventListener('mousemove', onMouseMove);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mouseleave', onMouseLeave);
      el.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('mousemove', onMouseMove);
    };
  }, [direction]);

  /* ── Mouse wheel → scroll horizontal ── */
  useEffect(() => {
    if (direction === 'vertical') return;
    const el = boardRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return; // sem overflow, ignora
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [direction]);
  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDragEnter = (columnId: string) => { if (draggedId) setDragOverColumn(columnId); };
  const handleDragLeave = (e: React.DragEvent, columnId: string) => {
    // Only clear if leaving the column element itself (not entering a child)
    const related = e.relatedTarget as Node | null;
    const current = e.currentTarget as Node;
    if (!current.contains(related)) setDragOverColumn(prev => prev === columnId ? null : prev);
  };

  const handleDrop = async (columnId: string) => {
    if (!draggedId) return;
    setDragOverColumn(null);
    try {
      await onDrop(draggedId, columnId);
    } finally {
      setDraggedId(null);
    }
  };

  const scrollToColumn = useCallback((columnId: string) => {
    const el = columnRefs.current.get(columnId);
    if (el && boardRef.current) {
      boardRef.current.scrollTo({
        left: el.offsetLeft - 12,
        behavior: 'smooth',
      });
    }
  }, []);

  const setColumnRef = useCallback((columnId: string, el: HTMLDivElement | null) => {
    if (el) columnRefs.current.set(columnId, el);
    else columnRefs.current.delete(columnId);
  }, []);

  /** Clicou na circle do header nav */
  const handleNavCircleClick = useCallback((columnId: string) => {
    if (scrolledColumn === columnId) {
      // Segundo clique → adicionar
      onAddClick?.(columnId);
      setScrolledColumn(null);
    } else {
      // Primeiro clique → scroll
      scrollToColumn(columnId);
      setScrolledColumn(columnId);
      // Reset após 4s se não clicar de novo
      setTimeout(() => setScrolledColumn(prev => prev === columnId ? null : prev), 4000);
    }
  }, [scrolledColumn, scrollToColumn, onAddClick]);

  return (
    <div className={`kb-container ${className}`} style={style}>

      {/* ── Barra de navegação interna (quando não está no toolbar) ── */}
      {!hideNav && !isLoading && (() => {
        // Calcular distribuição de volume para cores dos badges
        const counts = columns.map(col => items.filter(item => getColumnId(item) === col.id).length);
        const nonZero = counts.filter(c => c > 0);
        const avg = nonZero.length ? nonZero.reduce((a, b) => a + b, 0) / nonZero.length : 1;
        const stdDev = nonZero.length > 1
          ? Math.sqrt(nonZero.reduce((sum, c) => sum + (c - avg) ** 2, 0) / nonZero.length)
          : avg * 0.3;
        const safeStd = stdDev || avg * 0.2 || 1;

        /** Determina cor com base no z-score (desvios da média) */
        function getVolumeInfo(count: number): { bg: string; shadow: string; label: string } {
          if (count === 0) return { bg: '#cbd5e1', shadow: 'rgba(148,163,184,.3)', label: 'Vazio — nenhum item nesta etapa' };
          const z = (count - avg) / safeStd;
          if (z < -1.2) return { bg: '#e2e8f0', shadow: 'rgba(148,163,184,.3)', label: `Insignificante — muito abaixo da média (${Math.round(avg)})` };
          if (z < -0.4) return { bg: '#3b82f6', shadow: 'rgba(59,130,246,.4)', label: `Volume baixo — abaixo da média (${Math.round(avg)})` };
          if (z < 0.4)  return { bg: '#10b981', shadow: 'rgba(16,185,129,.4)', label: `Volume normal — próximo da média (${Math.round(avg)})` };
          if (z < 1.0)  return { bg: '#f59e0b', shadow: 'rgba(245,158,11,.4)', label: `Acima do desejado — acima da média (${Math.round(avg)})` };
          if (z < 1.8)  return { bg: '#ef4444', shadow: 'rgba(239,68,68,.4)', label: `Sobrecarga — volume muito acima da média (${Math.round(avg)})` };
          return { bg: '#1e293b', shadow: 'rgba(0,0,0,.5)', label: `Crítico — concentração excessiva (${count} vs média ${Math.round(avg)})` };
        }

        return (
          <div className="kb-nav-strip" style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, white)', borderRadius: '12px 12px 0 0' }}>
            {columns.map((column, idx) => {
              const count = counts[idx];
              const isEmpty = count === 0;
              const vol = getVolumeInfo(count);
              return (
                <button
                  key={column.id}
                  className={`kb-nav-item${isEmpty ? ' kb-nav-empty' : ''}`}
                  onClick={() => scrollToColumn(column.id)}
                  title={`${column.label} (${count}) — ${vol.label}`}
                >
                  <span className="kb-nav-circle" style={{ background: column.color }}>{String.fromCharCode(65 + idx)}</span>
                  <span className="kb-nav-label">{column.label}</span>
                  {count > 0 && (
                    <span
                      className="kb-nav-badge"
                      style={{
                        background: vol.bg,
                        boxShadow: `0 1px 4px ${vol.shadow}`,
                        color: vol.bg === '#e2e8f0' || vol.bg === '#cbd5e1' ? '#475569' : 'white',
                      }}
                      title={`${count} itens — ${vol.label}`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* ── Board container (horizontal ou vertical) ── */}
      <div className={`kb-board ${direction === 'vertical' ? 'kb-board-vertical' : ''}`} ref={boardRef}>
        {isLoading ? (
          <div className="dt-loading">{loadingText}</div>
        ) : (
          columns.map((column, idx) => {
            const columnItems = items.filter(item => getColumnId(item) === column.id);
            const isEmpty = columnItems.length === 0 && !disableCollapse;
            const isActive = activeColumn === column.id;

            /* ── Coluna colapsada (vazia) ── */
            if (isEmpty) {
              const isDragTarget = dragOverColumn === column.id;
              return (
                <div
                  key={column.id}
                  className={`kb-column kb-column-collapsed${isDragTarget ? ' kb-column-dragover' : ''}`}
                  style={{ '--kb-col-color': column.color } as React.CSSProperties}
                  data-column-id={column.id}
                  ref={(el) => setColumnRef(column.id, el)}
                  onDragOver={handleDragOver}
                  onDragEnter={() => handleDragEnter(column.id)}
                  onDragLeave={(e) => handleDragLeave(e, column.id)}
                  onDrop={() => handleDrop(column.id)}
                  title={`${column.label} — arraste cards para cá`}
                  onClick={onAddClick ? () => onAddClick(column.id) : undefined}
                >
                  <div className="kb-collapsed-indicator">
                    <span className="kb-collapsed-num kb-collapsed-num-addable" style={{ background: column.color }}>
                      <span className="kb-collapsed-num-text">{String.fromCharCode(65 + idx)}</span>
                      {onAddClick && <Plus size={14} className="kb-collapsed-num-plus" />}
                    </span>
                    <span className="kb-collapsed-label">{column.label}</span>
                  </div>
                  {/* Hidden header/cards kept for CSS rule consistency */}
                  <div className="kb-column-header" />
                  <div className="kb-cards" />
                </div>
              );
            }

            /* ── Coluna normal (com itens) ── */
            const isDragTarget = dragOverColumn === column.id;
            return (
              <div
                key={column.id}
                className={`kb-column${isActive ? ' kb-column-active' : ''}${isDragTarget ? ' kb-column-dragover' : ''}`}
                style={{ '--kb-col-color': column.color } as React.CSSProperties}
                data-column-id={column.id}
                ref={(el) => setColumnRef(column.id, el)}
                onDragOver={handleDragOver}
                onDragEnter={() => handleDragEnter(column.id)}
                onDragLeave={(e) => handleDragLeave(e, column.id)}
                onDrop={() => handleDrop(column.id)}
              >
                {/* Column Header */}
                <div className="kb-column-header">
                  <div className="kb-column-label">
                    <span
                      className="kb-column-num"
                      style={{ background: column.color }}
                    >
                      {String.fromCharCode(65 + idx)}
                    </span>
                    {column.label}
                  </div>
                  <div className="kb-column-header-right">
                    <span className="kb-column-count">{columnItems.length}</span>
                    {onAddClick && (
                      <button
                        className="kb-column-add-btn"
                        style={{ background: column.color }}
                        onClick={(e) => { e.stopPropagation(); onAddClick(column.id); }}
                        title={`Adicionar em ${column.label}`}
                      >
                        <Plus size={12} />
                      </button>
                    )}
                    {renderColumnHeaderRight?.(column)}
                  </div>
                </div>

                {/* Sub Header */}
                {renderColumnSubHeader?.(column)}

                {/* Cards */}
                <div className="kb-cards custom-scroll">
                  {columnItems.map(item => (
                    <div
                      key={item.id}
                      className="kb-card"
                      draggable
                      onDragStart={() => handleDragStart(item.id)}
                      onClick={onCardClick ? () => onCardClick(item) : undefined}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = column.color; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = ''; }}
                      style={onCardClick ? { cursor: 'pointer' } : undefined}
                    >
                      {renderCard(item, column)}
                    </div>
                  ))}
                </div>

                {/* Empty area no fundo para adicionar */}
                {onAddClick && (
                  <button
                    className="kb-column-empty-add"
                    onClick={() => onAddClick(column.id)}
                    style={{ '--kb-col-color': column.color } as React.CSSProperties}
                  >
                    <Plus size={16} />
                    <span>Adicionar</span>
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
